import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface GalleryItem {
  id: number;
  image_url: string;
  thumbnail_url?: string;
  title?: string;
  order: number;
}

interface ImageGallery {
  id: number;
  title: string;
  description?: string;
  cover_image?: string;
  category?: string;
  items?: GalleryItem[];
  items_count?: number;
  created_at: string;
}

const ImageGalleryList: React.FC = () => {
  const [galleries, setGalleries] = useState<ImageGallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/cms/image-galleries');
      setGalleries(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load galleries');
      console.error('Error fetching galleries:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...Array.from(new Set(galleries.map(g => g.category).filter((cat): cat is string => Boolean(cat))))];
  const filteredGalleries = selectedCategory === 'all' 
    ? galleries 
    : galleries.filter(g => g.category === selectedCategory);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <div className="loading-spinner"></div>
        <h3>{t('public.galleries.loading')}</h3>
        <p>{t('public.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '40px'
      }}>
        <h3>{t('public.galleries.errorTitle')}</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchGalleries}>
          {t('public.galleries.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="image-gallery-list" style={{ 
      minHeight: '80vh',
      background: '#f8f9fa'
    }}>
      {/* Hero Section */}
      <div style={{ 
        textAlign: 'center', 
        padding: '60px 20px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        marginBottom: '50px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            marginBottom: '20px',
            color: '#fff',
            fontWeight: '700'
          }}>
            {t('public.galleries.title')}
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6'
          }}>
            {t('public.galleries.description')}
          </p>
        </div>
      </div>

      <div style={{ 
        padding: '40px 20px', 
        maxWidth: '1200px', 
        margin: '0 auto' 
      }}>
        {/* Category Filter */}
        {categories.length > 1 && (
          <div style={{ 
            marginBottom: '30px',
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap'
          }}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '25px',
                  background: selectedCategory === cat ? '#667eea' : '#fff',
                  color: selectedCategory === cat ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s'
                }}
              >
                {cat === 'all' ? t('public.galleries.allCategories') : cat}
              </button>
            ))}
          </div>
        )}

        {/* Galleries Count */}
        {filteredGalleries.length > 0 && (
          <div style={{ 
            marginBottom: '30px',
            padding: '15px 20px',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <p style={{ 
              margin: 0, 
              fontSize: '1rem', 
              color: '#666',
              fontWeight: '500'
            }}>
              {filteredGalleries.length} {filteredGalleries.length === 1 ? 'Gallery' : 'Galleries'} Available
            </p>
          </div>
        )}

        {/* Galleries Grid - Facebook Album Style */}
        {filteredGalleries.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '80px 20px',
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <i className="fas fa-images" style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3, color: '#999' }}></i>
            <h3 style={{ color: '#333', marginBottom: '10px' }}>{t('public.galleries.emptyTitle')}</h3>
            <p style={{ color: '#666' }}>{t('public.galleries.emptyDescription')}</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: '25px'
          }}>
            {filteredGalleries.map((gallery) => {
              const imageCount = gallery.items?.length || gallery.items_count || 0;
              const coverImage = gallery.cover_image || gallery.items?.[0]?.thumbnail_url || gallery.items?.[0]?.image_url;
              
              return (
                <div 
                  key={gallery.id}
                  onClick={() => navigate(`/galleries/${gallery.id}`)}
                  style={{
                    background: '#fff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Cover Image */}
                  <div style={{
                    position: 'relative',
                    paddingBottom: '75%',
                    height: 0,
                    overflow: 'hidden',
                    background: '#f0f0f0'
                  }}>
                    {coverImage ? (
                      <img 
                        src={coverImage} 
                        alt={gallery.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#999',
                        fontSize: '48px'
                      }}>
                        <i className="fas fa-images"></i>
                      </div>
                    )}
                    {/* Image Count Badge */}
                    <div style={{
                      position: 'absolute',
                      bottom: '15px',
                      right: '15px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      padding: '8px 15px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <i className="fas fa-images"></i>
                      {imageCount}
                    </div>
                  </div>

                  {/* Gallery Info */}
                  <div style={{ padding: '20px' }}>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#333',
                      lineHeight: '1.4'
                    }}>
                      {gallery.title}
                    </h3>
                    {gallery.description && (
                      <p style={{ 
                        margin: '0 0 10px 0', 
                        fontSize: '14px', 
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.5'
                      }}>
                        {gallery.description}
                      </p>
                    )}
                    {gallery.category && (
                      <span style={{ 
                        display: 'inline-block',
                        background: '#e9ecef',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: '#666',
                        fontWeight: '500'
                      }}>
                        {gallery.category}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGalleryList;

